/**
 * Pure rules for the paged image library (`Query.myImages` + the selection mutations).
 *
 * Three things every consumer has to agree on, so they live in one place:
 *
 * 1. Selection math — what a checkbox grid's state turns into as a GraphQL
 *    `ImageSelection`, what count it displays, and the fixed-order delete plan for a
 *    selection that includes folders.
 * 2. Connection edge merge — the plain function the `Query.myImages` field policies
 *    (client and town-square) and `fetchMore` `updateQuery` callers use to append a page.
 * 3. Filter key — a stable string for an `ImageFilter` + `ImageSort`, used to compare
 *    filter states the way Apollo's `keyArgs` would and to pin a select-all.
 *
 * Lives in `shared/` because town-square has no test runner, and both apps must compute
 * the same selection. Framework-free on purpose: no React, no Apollo imports
 * (see docs/decisions/0002-shared-react-components.md).
 */

export type TagMatch = 'ALL' | 'ANY';
export type ImageVisibility = 'ALL' | 'PUBLIC' | 'PRIVATE';
export type ImageSortField = 'CREATED_AT' | 'TITLE' | 'FILE_NAME';
export type SortDirection = 'ASC' | 'DESC';

/** Mirror of the GraphQL `ImageFilter` input. */
export type ImageFilter = {
  query?: string | null;
  tagIds?: readonly string[] | null;
  tagMatch?: TagMatch | null;
  visibility?: ImageVisibility | null;
  collectionId?: string | null;
  nested?: boolean | null;
  unfiled?: boolean | null;
  statuses?: readonly string[] | null;
  contexts?: readonly string[] | null;
  ids?: readonly string[] | null;
};

/** Mirror of the GraphQL `ImageSort` input. */
export type ImageSort = {
  field?: ImageSortField | null;
  direction?: SortDirection | null;
};

/**
 * Mirror of the GraphQL `ImageSelection` input. `asOf` is a `DateTime` — always the
 * server's `ImageConnection.asOf`, never the client clock.
 */
export type ImageSelection = {
  ids?: string[];
  collectionIds?: string[];
  filter?: ImageFilter;
  exceptIds?: string[];
  asOf?: string;
};

/** Images and folders picked one by one. */
export type ExplicitSelection = {
  mode: 'explicit';
  ids: string[];
  collectionIds: string[];
};

/**
 * Select-all: every image matching `filter` as of the server `asOf`, minus `exceptIds`.
 * `key` is the filter key it was pinned to; `collectionIds` are the visible child folders;
 * `ids` are images ticked one by one outside the snapshot (uploaded after `asOf`).
 */
export type FilterSelection = {
  mode: 'filter';
  filter: ImageFilter;
  key: string;
  asOf: string;
  exceptIds: string[];
  ids: string[];
  collectionIds: string[];
};

export type LibrarySelection = ExplicitSelection | FilterSelection;

const FILTER_DEFAULTS: Partial<Record<keyof ImageFilter, unknown>> = {
  tagMatch: 'ANY',
  visibility: 'ALL',
  nested: false,
  unfiled: false,
};

const SORT_DEFAULTS: Partial<Record<keyof ImageSort, unknown>> = {
  field: 'CREATED_AT',
  direction: 'DESC',
};

function isEmpty(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  return Array.isArray(value) && value.length === 0;
}

/** Drops empty and default-valued fields, sorts keys and set-like arrays. */
function normalize<T extends object>(
  input: T | null | undefined,
  defaults: Partial<Record<keyof T, unknown>>
): Partial<T> {
  if (!input) {
    return {};
  }

  const keys = Object.keys(input).sort() as (keyof T)[];

  return keys.reduce<Partial<T>>((memo, key) => {
    const value = input[key];
    if (isEmpty(value) || defaults[key] === value) {
      return memo;
    }

    memo[key] = (Array.isArray(value) ? [...value].sort() : value) as T[keyof T];
    return memo;
  }, {});
}

/** The filter with empty and default-valued fields dropped — the canonical form to send and snapshot. */
export function normalizeFilter(filter: ImageFilter | null | undefined): ImageFilter {
  return normalize(filter, FILTER_DEFAULTS);
}

/**
 * Stable key for a filter + sort: key order, empty fields, default-valued fields and the
 * order of id/status/context arrays do not change it.
 */
export function filterKey(filter?: ImageFilter | null, sort?: ImageSort | null): string {
  return JSON.stringify({ filter: normalizeFilter(filter), sort: normalize(sort, SORT_DEFAULTS) });
}

export function emptySelection(): ExplicitSelection {
  return { mode: 'explicit', ids: [], collectionIds: [] };
}

/**
 * Select every image matching `filter`, pinned to its key and to the server `asOf` of the
 * connection's current first page. `collectionIds` are the visible child folders.
 */
export function selectAll(params: {
  filter: ImageFilter | null | undefined;
  sort?: ImageSort | null;
  asOf: string;
  collectionIds?: readonly string[];
}): FilterSelection {
  return {
    mode: 'filter',
    filter: normalizeFilter(params.filter),
    key: filterKey(params.filter, params.sort),
    asOf: params.asOf,
    exceptIds: [],
    ids: [],
    collectionIds: [...(params.collectionIds ?? [])],
  };
}

/**
 * Clears a select-all once the active filter key (folder, search, tags, tag mode,
 * visibility, sort) no longer matches the one it was pinned to. Explicit picks are concrete
 * ids and are left alone.
 */
export function clearIfStale(selection: LibrarySelection, key: string): LibrarySelection {
  if (selection.mode !== 'filter' || selection.key === key) {
    return selection;
  }

  return emptySelection();
}

function toggle(list: readonly string[], id: string): string[] {
  if (list.includes(id)) {
    return list.filter((item) => item !== id);
  }

  return [...list, id];
}

/** Explicit mode: add/remove the pick. Filter mode: add/remove the exception. */
export function toggleImage(selection: LibrarySelection, id: string): LibrarySelection {
  if (selection.mode === 'filter') {
    return { ...selection, exceptIds: toggle(selection.exceptIds, id) };
  }

  return { ...selection, ids: toggle(selection.ids, id) };
}

export function toggleFolder(selection: LibrarySelection, id: string): LibrarySelection {
  return { ...selection, collectionIds: toggle(selection.collectionIds, id) };
}

export function isImageSelected(selection: LibrarySelection, id: string) {
  if (selection.ids.includes(id)) {
    return true;
  }

  return selection.mode === 'filter' && !selection.exceptIds.includes(id);
}

/** The GraphQL `ImageSelection` input for the selection. */
export function toSelectionInput(selection: LibrarySelection): ImageSelection {
  if (selection.mode === 'explicit') {
    return { ids: [...selection.ids], collectionIds: [...selection.collectionIds] };
  }

  const input: ImageSelection = {
    filter: selection.filter,
    exceptIds: [...selection.exceptIds],
    asOf: selection.asOf,
  };
  if (selection.ids.length > 0) {
    input.ids = [...selection.ids];
  }
  if (selection.collectionIds.length > 0) {
    input.collectionIds = [...selection.collectionIds];
  }

  return input;
}

/** Ticked under select-all: an extra pick, or a snapshot image that was not unchecked. */
function inSelectAll(selection: FilterSelection, snapshot: ReadonlySet<string>, id: string) {
  if (selection.ids.includes(id)) {
    return true;
  }

  return snapshot.has(id) && !selection.exceptIds.includes(id);
}

/**
 * The `moveImages` selection for dragged image ids: the select-all (without its folders)
 * when every dragged id is ticked in it - the artist dragged the selection - otherwise
 * exactly those ids. `snapshotIds` are the loaded images the filter snapshot covers, so
 * dragging one unticked tile, even a newer upload outside the snapshot, never widens to
 * the whole filter.
 */
export function moveSelection(
  imageIds: readonly string[],
  selection: FilterSelection | null,
  snapshotIds: readonly string[]
): ImageSelection {
  if (!selection) {
    return { ids: [...imageIds] };
  }

  const snapshot = new Set(snapshotIds);
  if (!imageIds.every((id) => inSelectAll(selection, snapshot, id))) {
    return { ids: [...imageIds] };
  }

  return toSelectionInput({ ...selection, collectionIds: [] });
}

/** Displayed count: filter mode is `totalCount - exceptIds` plus the selected folders. */
export function selectedCount(selection: LibrarySelection, totalCount: number) {
  if (selection.mode === 'explicit') {
    return selection.ids.length + selection.collectionIds.length;
  }

  return Math.max(0, totalCount - selection.exceptIds.length) + selection.collectionIds.length;
}

/** "Select all 7,828" or "Select all 7,828 + 3 folders". */
export function selectAllLabel(totalCount: number, folderCount: number) {
  const label = `Select all ${totalCount.toLocaleString('en-US')}`;
  if (folderCount <= 0) {
    return label;
  }

  return `${label} + ${folderCount.toLocaleString('en-US')} ${folderCount === 1 ? 'folder' : 'folders'}`;
}

function hasImages(selection: LibrarySelection) {
  if (selection.mode === 'filter') {
    return true;
  }

  return selection.ids.length > 0;
}

export type DeleteStepState = 'pending' | 'done' | 'failed';

export type DeleteStep =
  | { id: 'removeImages'; kind: 'removeImages'; selection: ImageSelection; state: DeleteStepState }
  | {
      id: string;
      kind: 'moveImages';
      folderId: string;
      selection: ImageSelection;
      collectionId: string | null;
      state: DeleteStepState;
    }
  | { id: string; kind: 'deleteCollection'; folderId: string; state: DeleteStepState };

/**
 * The fixed-order steps for deleting `selection`: first `removeImages` for the selected
 * images WITHOUT `collectionIds` (skipped when no image is selected), then per folder
 * `moveImages({ filter: { collectionId: folder, nested: true } }, parentId)` followed by
 * `deleteCollection`, so every image in a deleted folder survives in the parent. A filter,
 * not `collectionIds`: those resolve processed images only, and an upload still in flight
 * must move too. `parentId` null = root.
 */
export function deletePlan(selection: LibrarySelection, parentId: string | null): DeleteStep[] {
  const images = toSelectionInput({ ...selection, collectionIds: [] });
  delete images.collectionIds;
  const head: DeleteStep[] = hasImages(selection)
    ? [{ id: 'removeImages', kind: 'removeImages', selection: images, state: 'pending' }]
    : [];

  return selection.collectionIds.reduce<DeleteStep[]>((memo, folderId) => {
    memo.push(
      {
        id: `moveImages:${folderId}`,
        kind: 'moveImages',
        folderId,
        selection: { filter: { collectionId: folderId, nested: true } },
        collectionId: parentId,
        state: 'pending',
      },
      { id: `deleteCollection:${folderId}`, kind: 'deleteCollection', folderId, state: 'pending' }
    );
    return memo;
  }, head);
}

export function markStep(plan: readonly DeleteStep[], id: string, state: DeleteStepState): DeleteStep[] {
  return plan.map((step) => {
    if (step.id !== id) {
      return step;
    }

    return { ...step, state };
  });
}

/**
 * The steps still to run, in order: failed or never-run ones. `removeImages` is never
 * re-offered once it is done or once any folder move has committed, and a folder's
 * `deleteCollection` always follows its own move.
 */
export function nextSteps(plan: readonly DeleteStep[]): DeleteStep[] {
  const moved = plan.some((step) => step.kind === 'moveImages' && step.state === 'done');

  return plan.filter((step) => {
    if (step.state === 'done') {
      return false;
    }

    return !(step.kind === 'removeImages' && moved);
  });
}

/** An edge whose node carries its id directly (query results, `updateQuery`). */
export type IdentifiedEdge = { node: { id: string } };

function nodeId(edge: IdentifiedEdge) {
  return edge.node.id;
}

/**
 * Appends `incoming` edges to `existing`, dropping any whose node id is already present
 * (or repeats within the page), so cursor order is preserved. Field policies pass `idOf`
 * built on `readField('id', edge.node)` since cached nodes are references.
 */
export function mergeEdges<E extends IdentifiedEdge>(
  existing: readonly E[] | null | undefined,
  incoming: readonly E[]
): E[];
export function mergeEdges<E>(
  existing: readonly E[] | null | undefined,
  incoming: readonly E[],
  idOf: (edge: E) => string | null | undefined
): E[];
export function mergeEdges<E>(
  existing: readonly E[] | null | undefined,
  incoming: readonly E[],
  idOf: (edge: E) => string | null | undefined = nodeId as unknown as (edge: E) => string
): E[] {
  const seen = new Set<string>();

  return [...(existing ?? []), ...incoming].reduce<E[]>((memo, edge) => {
    const id = idOf(edge);
    if (id === null || id === undefined) {
      memo.push(edge);
      return memo;
    }

    if (seen.has(id)) {
      return memo;
    }

    seen.add(id);
    memo.push(edge);
    return memo;
  }, []);
}

export type ConnectionLike<E> = { edges: readonly E[]; asOf?: unknown };

/**
 * Merges one page into a cached connection. No `after` cursor means a fresh first page,
 * which replaces the cache (new totals, new `asOf`). An appended page takes the incoming
 * `pageInfo`/`totalCount` but keeps the first page's `asOf`, which is what select-all pins.
 */
export function mergeConnection<C extends ConnectionLike<E>, E>(
  existing: C | null | undefined,
  incoming: C,
  options: { after?: string | null; idOf: (edge: E) => string | null | undefined }
): C {
  if (!existing || !options.after) {
    return incoming;
  }

  const merged: C = { ...incoming, edges: mergeEdges(existing.edges, incoming.edges, options.idOf) };
  if (existing.asOf !== undefined) {
    merged.asOf = existing.asOf;
  }

  return merged;
}
